import type { RouteLocationRaw } from 'vue-router'
import domain from '../server-config/domain.config'
import { zeroToLocalZonetime } from './timezone'
import { setUrlCache } from '@/api/models/system'
import md5 from 'js-md5'
import {
  parseSourceToMeta,
  parseSourceToParts,
  getTrafficSourceParts,
  resolveCurrentPageCode,
  type ISourceMeta
} from '@/utils/traffic-source'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import type { IShareLinkType } from '@/api/models/share'
import { transformImageUrl, type IImageTransformConfig } from '../utils/image-url'

const monthEN: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Spt', 'Oct', 'Nov', 'Dec']
/**
  * 获取变量类型（比typeof更加准确，能够区分 Array | Object 等特殊对象）
  * @param {String} val
  * @returns 类型
*/
export const getTypeof = (val: any): string => Object.prototype.toString.call(val).slice(8, -1)

export interface ITrafficSourceTracking {
  internalLatestTrafficSourceModule?: string
  internalLatestTrafficSourceType?: string
  insideLatestTrafficSourceValue?: string
  internalFirstTrafficSourcePage?: string
  internalLatestTrafficSourcePage?: string
  algorithmModelName?: string
  isAd?: boolean
  isNewTag?: boolean
  isChoiceTag?: boolean
  requestId?: string
}

export interface IToListingQuery {
  title?: string
  shortTitle?: string
  source?: string
  type?: 'sku',
  skuId?: number
  from?: 'flash-sale'
  addressId?: number
  isAd?: 'ad'
  isNewTag?: boolean
  isChoiceTag?: boolean
  requestId?: string
  algorithmModelName?: string
}

/** 将 recent-views 等接口返回的 trafficSourceTracking 转为 toListingDetail / set-params 参数 */
export const getProductTrafficSourceTracking = (
  product?: Record<string, unknown> | null
): string | ITrafficSourceTracking | null | undefined => {
  if (!product) return undefined
  if (product.trafficSourceTracking) {
    return product.trafficSourceTracking as string | ITrafficSourceTracking
  }
  const key = Object.keys(product).find(k => k.toLowerCase() === 'trafficsourcetracking')
  return key ? product[key] as string | ITrafficSourceTracking : undefined
}

const normalizeTrafficSourceTracking = (
  raw: Record<string, unknown>
): ITrafficSourceTracking => {
  const map: Record<string, unknown> = {}
  Object.entries(raw).forEach(([k, v]) => {
    map[k.toLowerCase()] = v
  })

  return {
    internalLatestTrafficSourceModule: String(map.internallatesttrafficsourcemodule || ''),
    internalLatestTrafficSourceType: String(map.internallatesttrafficsourcetype || ''),
    insideLatestTrafficSourceValue: String(map.insidelatesttrafficsourcevalue || ''),
    internalFirstTrafficSourcePage: String(map.internalfirsttrafficsourcepage || ''),
    internalLatestTrafficSourcePage: String(map.internallatesttrafficsourcepage || ''),
    algorithmModelName: String(map.algorithmmodelname || ''),
    isAd: map.isad === true || map.isad === 'true',
    isNewTag: map.isnewtag === true || map.isnewtag === 'true',
    isChoiceTag: map.ischoicetag === true || map.ischoicetag === 'true',
    requestId: String(map.requestid || '')
  }
}

export const RECENT_VIEW_LATEST_TRAFFIC_SOURCE_PAGE = 'recentViewed'
export const ORDER_DETAIL_LATEST_TRAFFIC_SOURCE_PAGE = 'orderDetails'
export const PRODUCT_REVIEW_DETAIL_LATEST_TRAFFIC_SOURCE_PAGE = 'productReviewDetails'

/** 是否严选商品（highlights 含 Choice） */
export const resolveIsChoiceTag = (highlights?: string[] | null) =>
  Array.isArray(highlights) && highlights.some(item => item.includes('Choice'))

/** 是否秒杀商品（highlights 含 FLASH_SALE） */
export const resolveIsFlashSaleTag = (highlights?: string[] | null) =>
  Array.isArray(highlights) && highlights.includes('FLASH_SALE')

export const FLASH_SALE_BADGE_URL = 'http://image.kilimall.com/c/h5/ic_flash_sale.png'

/** 大促 highlights → Icon 映射（按展示优先级排序） */
export const PROMO_BADGE_MAP: { key: string; url: string; alt: string }[] = [
  { key: 'ANNIVERSARY', url: 'https://image.kilimall.com/c/h5/activity/ic_anniv.png', alt: 'Anniversary' },
  { key: 'REGULAR_PROMO', url: 'https://image.kilimall.com/c/h5/activity/ic_sale.png', alt: 'Sale' },
  { key: 'XMAS', url: 'https://image.kilimall.com/c/h5/activity/ic_xmas.png', alt: 'Xmas' },
  { key: 'BLACK_FRIDAY', url: 'https://image.kilimall.com/c/h5/activity/ic_black_fri.png', alt: 'Black Friday' }
]

/** 根据 highlights 解析需展示的大促 Icon 列表 */
export const resolvePromoBadges = (highlights?: string[] | null) => {
  if (!Array.isArray(highlights) || !highlights.length) return []
  return PROMO_BADGE_MAP.filter(item => highlights.includes(item.key))
}

export interface ITrafficTrackFields {
  source?: string
  isAd?: boolean | string
  isNewTag?: boolean
  isChoiceTag?: boolean
  requestId?: string
  algorithmModelName?: string
}

/** 路由 query ↔ 埋点字段互转（评论页链路传递） */
export const getTrafficTrackFromRoute = (
  query: Record<string, unknown> = {}
): ITrafficTrackFields => ({
  source: (query.source as string) || '',
  isAd: query.isAd as string | boolean,
  isNewTag: query.isNewTag === 'true' || query.isNewTag === true,
  isChoiceTag: query.isChoiceTag === 'true' || query.isChoiceTag === true,
  requestId: (query.requestId as string) || '',
  algorithmModelName: (query.algorithmModelName as string) || ''
})

export const trafficTrackToRouteQuery = (
  track: ITrafficTrackFields
): Record<string, string> => {
  const query: Record<string, string> = {}
  if (track.source) query.source = track.source
  if (track.isAd === true || track.isAd === 'ad') query.isAd = 'ad'
  if (track.isNewTag) query.isNewTag = 'true'
  if (track.isChoiceTag) query.isChoiceTag = 'true'
  if (track.requestId) query.requestId = track.requestId
  if (track.algorithmModelName) query.algorithmModelName = track.algorithmModelName
  return query
}

/** 由 source 等埋点字段构建 toListingDetail / set-params 参数 */
export const buildListingQueryFromTrackFields = (
  fields?: ITrafficTrackFields | null,
  options?: IParseTrafficSourceOptions
): Partial<IToListingQuery> => {
  if (!fields?.source) return {}

  const pipeSource = fields.source.includes('|')
    ? fields.source
    : fields.source.split('-').join('|')

  const sourceParts = pipeSource.split('|')
  while (sourceParts.length < 5) sourceParts.push('')
  if (options?.latestTrafficSourcePage) {
    sourceParts[4] = options.latestTrafficSourcePage
  }

  const source = sourceParts.join('|')
  if (!source.replace(/\|/g, '')) return {}

  return {
    source,
    isAd: fields.isAd === 'ad' || fields.isAd === true ? 'ad' : undefined,
    isNewTag: fields.isNewTag,
    isChoiceTag: fields.isChoiceTag,
    requestId: fields.requestId || '',
    algorithmModelName: fields.algorithmModelName || ''
  }
}

export interface IParseTrafficSourceOptions {
  /** 覆盖 source 第 5 段 internalLatestTrafficSourcePage */
  latestTrafficSourcePage?: string
}

export const parseTrafficSourceTrackingToListingQuery = (
  trafficSourceTracking?: string | ITrafficSourceTracking | Record<string, unknown> | null,
  options?: IParseTrafficSourceOptions
): Partial<IToListingQuery> => {
  if (!trafficSourceTracking) return {}

  let tracking: ITrafficSourceTracking
  if (typeof trafficSourceTracking === 'string') {
    try {
      tracking = normalizeTrafficSourceTracking(JSON.parse(trafficSourceTracking))
    } catch {
      return {}
    }
  } else {
    tracking = normalizeTrafficSourceTracking(trafficSourceTracking as Record<string, unknown>)
  }

  const sourceParts = [
    tracking.internalLatestTrafficSourceModule || '',
    tracking.internalLatestTrafficSourceType || '',
    tracking.insideLatestTrafficSourceValue || '',
    tracking.internalFirstTrafficSourcePage || '',
    options?.latestTrafficSourcePage || tracking.internalLatestTrafficSourcePage || ''
  ]
  const source = sourceParts.join('|')

  if (!source.replace(/\|/g, '')) {
    return {}
  }

  return {
    source,
    isAd: tracking.isAd ? 'ad' : undefined,
    isNewTag: tracking.isNewTag,
    isChoiceTag: tracking.isChoiceTag,
    requestId: tracking.requestId || '',
    algorithmModelName: tracking.algorithmModelName || ''
  }
}

/** 秒杀页跳转商品详情 source（5 段，| 分隔，写入 url-cache 时转为 -） */
export const buildFlashSaleListingSource = (
  sessionLabel: string,
  categoryName: string,
  firstPage = 'home',
  route?: Pick<RouteLocationNormalizedLoaded, 'query'>,
  latestPage = 'flashSale'
) => {
  const query = route?.query || useRoute().query
  const shareSource = buildShareEntryListingSourceFromQuery(query as Record<string, unknown>)
  if (shareSource) return shareSource

  const [module, type, , , queryLatestPage] = parseSourceToParts(String(query.source || ''))
  if (module === 'messageCenter' && type === 'promotionsMessage' && queryLatestPage === 'flashSale') {
    return buildMessageCenterPromotionSource('', 'flashSale')
  }

  return [
    'flashSale',
    sessionLabel || '',
    categoryName || '',
    firstPage,
    latestPage
  ].join('|')
}

export type MainIconBannerLatestPage = 'store' | 'searchProductResults' | 'searchStart'

/** 首页金刚区 cmsGoTo 跳转类型 → latest_page */
export const getMainIconBannerLatestPage = (cmsType: string): MainIconBannerLatestPage | '' => {
  switch (cmsType) {
    case 'store':
      return 'store'
    case 'key':
    case 'category':
    case 'brand':
      return 'searchProductResults'
    case 'product':
      return 'searchStart'
    default:
      return ''
  }
}

/** hotKeysJump linkType → latest_page */
export const getHotKeysLinkTypeLatestPage = (linkType: number): MainIconBannerLatestPage | '' => {
  switch (linkType) {
    case 6:
      return 'store'
    case 2:
    case 3:
      return 'searchProductResults'
    case 5:
    case 10:
      return 'searchStart'
    default:
      return ''
  }
}

/** 按跳转目标修正 source：补齐 5 段；空 firstPage 按当前路由补；latestPage 按 linkType */
export const applyHotKeysSourceLatestPage = (source: string, linkType: number): string => {
  if (!source) return source

  const latestPage = getHotKeysLinkTypeLatestPage(linkType)
  const parts = source.split('|')
  while (parts.length < 5) parts.push('')
  // 未显式传 firstPage 时补当前页（对齐 WAP / PC 普通搜索：searchProduct|type|value|firstPage|latestPage）
  if (!parts[3]) {
    parts[3] = resolveCurrentPageCode() || 'home'
  }
  if (latestPage) {
    parts[4] = latestPage
  }
  return parts.slice(0, 5).join('|')
}

/** 专题页 latest_page（/special、/c） */
export const MARKETING_CMS_LATEST_PAGE = 'marketingCms'

/** 是否专题页路径（/special、/c） */
export const isMarketingCmsUrl = (url?: string): boolean => {
  if (!url) return false
  try {
    const path = /^http(s)?:\/\//.test(url)
      ? new URL(url, typeof location !== 'undefined' ? location.origin : 'https://local.invalid').pathname
      : (url.split('?')[0] || '')
    return /^\/(special|c)(\/|$)/.test(path)
  } catch {
    return /^\/(special|c)(\/|$)/.test((url.split('?')[0] || ''))
  }
}

/** 覆盖 source 第 5 段 latestPage；空 firstPage 时补当前页 */
export const applySourceLatestPage = (source: string, latestPage: string): string => {
  if (!source || !latestPage) return source
  const parts = source.split('|')
  while (parts.length < 5) parts.push('')
  if (!parts[3]) {
    parts[3] = resolveCurrentPageCode() || 'home'
  }
  parts[4] = latestPage
  return parts.slice(0, 5).join('|')
}

/** 给 URL 追加/覆盖 query（保留原有参数） */
export const appendQueryToUrl = (url: string, query: Record<string, string>): string => {
  if (!url || !Object.keys(query).length) return url
  try {
    const isAbsolute = /^http(s)?:\/\//.test(url)
    const u = isAbsolute
      ? new URL(url)
      : new URL(url, typeof location !== 'undefined' ? location.origin : 'https://local.invalid')
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') u.searchParams.set(k, v)
    })
    if (isAbsolute) return u.toString()
    return `${u.pathname}${u.search}${u.hash}`
  } catch {
    const qs = Object.entries(query)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&')
    if (!qs) return url
    return url.includes('?') ? `${url}&${qs}` : `${url}?${qs}`
  }
}

/** 跳转专题页时：前 4 段沿用原 source，latestPage 固定 marketingCms，并写入 URL */
export const withMarketingCmsSource = (url: string, source?: string): string => {
  if (!url || !source || !isMarketingCmsUrl(url)) return url
  return appendQueryToUrl(url, {
    source: applySourceLatestPage(source, MARKETING_CMS_LATEST_PAGE)
  })
}

export const WHATS_NEW_FIRST_TRAFFIC_SOURCE_PAGE = 'whatsNew'

/** 从图片 URL 提取文件名（不含 query / hash） */
export const getImageFileNameFromUrl = (url?: string): string => {
  if (!url) return ''
  const path = url.split('?')[0].split('#')[0]
  const name = path.split('/').filter(Boolean).pop() || ''
  try {
    return decodeURIComponent(name)
  } catch {
    return name
  }
}

/** Banner 5 段 source（| 分隔） */
export const buildBannerSource = (
  trafficType: 'mainIconBanner' | 'centerBanner' | 'homeNavBottomSmallBanner' | 'headerTopBanner',
  sourceValue: string,
  firstPage: string,
  destinationType: string | number
) => {
  const latestPage = typeof destinationType === 'number'
    ? getHotKeysLinkTypeLatestPage(destinationType)
    : getMainIconBannerLatestPage(destinationType)

  return [
    'Banner',
    trafficType,
    sourceValue || '',
    firstPage,
    latestPage
  ].join('|')
}

/** 首页 Banner / 金刚区 source（5 段，| 分隔） */
export const buildHomeBannerSource = (
  trafficType: 'mainIconBanner' | 'centerBanner' | 'homeNavBottomSmallBanner' | 'headerTopBanner',
  sourceValue: string,
  cmsType: string | number
) => buildBannerSource(trafficType, sourceValue, 'home', cmsType)

/** 新品频道 Banner source */
export const buildWhatsNewBannerSource = (sourceValue: string, linkType: number) =>
  buildBannerSource('centerBanner', sourceValue, WHATS_NEW_FIRST_TRAFFIC_SOURCE_PAGE, linkType)

/** 新品频道 Full Banner source（5 段，| 分隔）；module 为 Banner，与首页 Banner 规范一致；source_value 为 bannerID_名称 */
export const buildWhatsNewFullBannerSource = (
  sourceValue: string,
  firstPage = WHATS_NEW_FIRST_TRAFFIC_SOURCE_PAGE,
  latestPage = WHATS_NEW_FIRST_TRAFFIC_SOURCE_PAGE
) => [
  'Banner',
  'whatsNewFullBanner',
  sourceValue || '',
  firstPage,
  latestPage
].join('|')

/** Banner source_value：{bannerID_名称} */
export const buildBannerIdNameSourceValue = (banner: Pick<IBanner, 'id' | 'name'>) =>
  `${banner.id ?? ''}_${banner.name || ''}`

/** @deprecated 使用 buildBannerIdNameSourceValue */
export const buildWhatsNewBannerSourceValue = buildBannerIdNameSourceValue

/** @deprecated 使用 buildWhatsNewFullBannerSource */
export const buildWhatsNewFullBannerListingSource = (
  sourceValue: string,
  firstPage = WHATS_NEW_FIRST_TRAFFIC_SOURCE_PAGE
) => buildWhatsNewFullBannerSource(sourceValue, firstPage, WHATS_NEW_FIRST_TRAFFIC_SOURCE_PAGE)

/** 首页推荐商品 source（5 段，| 分隔） */
export const buildHomeRecommendSource = (modelName = '') => [
  'recommend',
  'homeRecommend',
  modelName || '',
  'home',
  'home'
].join('|')

/** 购物车推荐商品 source（5 段，| 分隔） */
export const buildCartRecommendSource = (modelName = '') => [
  'recommend',
  'cartRecommend',
  modelName || '',
  'cart',
  'cart'
].join('|')

/** 购物车缺货 Similar 搜索 source（5 段，| 分隔）；source_value 为类目 id */
export const buildCartSimilarSearchSource = (categoryId: string | number = '', firstPage = 'cart') => [
  'searchProduct',
  'cartSimilarSearch',
  String(categoryId || ''),
  firstPage || 'cart',
  'searchProductResults'
].join('|')

/** 个人中心推荐商品 source（5 段，| 分隔） */
export const buildAccountRecommendSource = (modelName = '') => [
  'recommend',
  'accountRecommend',
  modelName || '',
  'accountCenter',
  'accountCenter'
].join('|')

/** 订单详情推荐商品 source（5 段，| 分隔） */
export const buildOrderDetailRecommendSource = (modelName = '') => [
  'recommend',
  'orderDetailRecommend',
  modelName || '',
  ORDER_DETAIL_LATEST_TRAFFIC_SOURCE_PAGE,
  ORDER_DETAIL_LATEST_TRAFFIC_SOURCE_PAGE
].join('|')

/** 新品推荐商品 source（5 段，| 分隔）；默认 first/latest 为 whatsNew（新品频道页） */
export const buildWhatsNewRecommendSource = (
  modelName = '',
  firstPage = WHATS_NEW_FIRST_TRAFFIC_SOURCE_PAGE,
  latestPage = firstPage
) => [
  'recommend',
  'whatsNewRecommend',
  modelName || '',
  firstPage,
  latestPage
].join('|')

/** 新品频道页 → 商详 source（5 段，| 分隔） */
export const buildWhatsNewPageListingSource = (
  categoryName = '',
  firstPage = '',
  route?: Pick<RouteLocationNormalizedLoaded, 'query'>
) => {
  const query = route?.query || useRoute().query
  const shareSource = buildShareEntryListingSourceFromQuery(query as Record<string, unknown>)
  if (shareSource) return shareSource

  return [
    'whatsNew',
    '',
    categoryName || '',
    firstPage,
    'whatsNew'
  ].join('|')
}

/** 新品频道页 first_page：由首页跳转时在 URL 携带 first_page=home */
export const resolveWhatsNewPageFirstPage = (route = useRoute()): string => {
  return String(route.query.first_page || '') === 'home' ? 'home' : ''
}

export type CrossStoreSpendSaveFirstPage = 'cart' | 'productDetail'

export const CROSS_STORE_SPEND_SAVE_LATEST_PAGE = 'crossStoreSpendSave'

/** 凑单页 first_page：商详 cart / 购物车 productDetail，由入口 URL 携带 */
export const resolveCrossStoreSpendSaveFirstPage = (
  route?: Pick<RouteLocationNormalizedLoaded, 'query'>
): CrossStoreSpendSaveFirstPage => {
  const query = route?.query || useRoute().query
  return String(query.first_page || '') === 'productDetail' ? 'productDetail' : 'cart'
}

/** 凑单页 source_value：搜索词 > 类目名 > all */
export const resolveCrossStoreSpendSaveSourceValue = (
  keyword?: string,
  categoryIds?: Array<number | string>,
  filter?: { categories?: Array<{ subCategories?: Array<{ id: number; name: string }> }> }
): string => {
  const kw = String(keyword || '').trim()
  if (kw) return kw

  const ids = (categoryIds || []).map(Number).filter(Boolean)
  const subCategories = filter?.categories?.[0]?.subCategories
  if (ids.length && subCategories?.length) {
    const cat = subCategories.find(c => c.id === ids[0])
    if (cat?.name) return cat.name
  }

  return 'all'
}

/** 凑单页 → 商详 source（5 段，| 分隔） */
export const buildCrossStoreSpendSaveListingSource = (
  sourceValue = 'all',
  firstPage: CrossStoreSpendSaveFirstPage = 'cart'
) => {
  const sourceType = firstPage === 'productDetail'
    ? 'productDetailCrossStoreSpendSave'
    : 'cartCrossStoreSpendSave'

  return [
    'crossStoreSpendSave',
    sourceType,
    sourceValue || 'all',
    firstPage,
    CROSS_STORE_SPEND_SAVE_LATEST_PAGE
  ].join('|')
}

export type VoucherSourceScene = 'voucherCenter' | 'myVoucher'
export type VoucherLatestPage = 'searchProductResults' | 'store' | 'productDetail' | 'voucherList'

/** 优惠券 source_value：优惠券 ID + 名称 */
export const buildVoucherSourceValue = (item: {
  voucherId?: number
  genId?: number | string
  name?: string
}) => {
  const id = item.voucherId || item.genId || ''
  return `${id}+${item.name || ''}`
}

/** 优惠券 → 搜索/店铺/商详 source（5 段，| 分隔） */
export const buildVoucherListingSource = (
  sourceScene: VoucherSourceScene,
  sourceValue: string,
  latestPage: VoucherLatestPage
) => [
  'voucher',
  sourceScene,
  sourceValue || '',
  'accountCenter',
  latestPage
].join('|')

export type ReviewsPublishStatus = 'pendingReview' | 'reviewed'

/** 评价中心 → 商详 source（5 段，| 分隔） */
export const buildReviewsPublishListingSource = (
  status: ReviewsPublishStatus
) => [
  'review',
  'myReview',
  status,
  'accountCenter',
  status === 'pendingReview' ? 'pendingReviewList' : 'reviewedList'
].join('|')

export const MESSAGE_CENTER_COMMENTS_REVIEW_FROM = 'messageCenterComments'

/** 是否从站内信 Comments 进入评论详情页 */
export const isMessageCenterCommentsReviewEntry = (
  query: Record<string, unknown> = {}
) => String(query.from || '') === MESSAGE_CENTER_COMMENTS_REVIEW_FROM

/** 站内信 Comments → 评论详情 → 商详 source（5 段，| 分隔） */
export const buildMessageCenterCommentsListingSource = () => [
  'messageCenter',
  'commentsMessage',
  '',
  '',
  PRODUCT_REVIEW_DETAIL_LATEST_TRAFFIC_SOURCE_PAGE
].join('|')

export type MessageCenterSourceType = 'commentsMessage' | 'promotionsMessage'
export type MessageCenterPromotionLatestPage =
  | 'searchProductResults'
  | 'productDetail'
  | 'store'
  | 'flashSale'
  | 'promotionsMessage'
  | typeof MARKETING_CMS_LATEST_PAGE

/** 站内信通用 5 段 source（| 分隔） */
export const buildMessageCenterSource = (
  sourceType: MessageCenterSourceType,
  sourceValue = '',
  latestPage: string
) => [
  'messageCenter',
  sourceType,
  sourceValue || '',
  '',
  latestPage
].join('|')

/** 根据站内信页 title / tab 解析 sourceType */
export const resolveMessageCenterSourceType = (
  pageTitle?: string | string[] | null,
  msgSourceType?: number | string | null,
  tabs?: Array<{ msgSourceType?: number; title?: string }> | null
): MessageCenterSourceType => {
  const title = Array.isArray(pageTitle) ? pageTitle[0] : pageTitle
  if (String(title || '') === 'Comments') return 'commentsMessage'

  if (msgSourceType != null && tabs?.length) {
    const tab = tabs.find(c => Number(c.msgSourceType) === Number(msgSourceType))
    if (tab?.title === 'Comments') return 'commentsMessage'
  }

  return 'promotionsMessage'
}

/** 站内信促销消息 → 搜索/商详 source（5 段，| 分隔） */
export const buildMessageCenterPromotionSource = (
  sourceValue = '',
  latestPage: MessageCenterPromotionLatestPage
) => buildMessageCenterSource('promotionsMessage', sourceValue, latestPage)

/** 是否从站内信促销消息进入全品类 → 搜索结果链路 */
export const isMessageCenterCategorySearchEntry = (
  route?: Pick<RouteLocationNormalizedLoaded, 'query'>
) => {
  const query = route?.query || useRoute().query
  const [module, type, , , latestPage] = parseSourceToParts(String(query.source || ''))
  return module === 'messageCenter' && type === 'promotionsMessage' && latestPage === 'searchProductResults'
}

/** 站内信 → 全品类 → 搜索结果/商详 source（5 段，| 分隔） */
export const buildMessageCenterCategorySearchSource = (categoryName = '') =>
  buildMessageCenterPromotionSource(categoryName, 'searchProductResults')

export type LiveChatFirstPage = 'productDetail' | 'store' | 'messageCenter'

export const LIVE_CHAT_LATEST_PAGE = 'liveChat'

/** 从进入聊天的 query 推断 first_page */
export const resolveLiveChatFirstPageFromQuery = (
  query: Record<string, unknown> = {}
): LiveChatFirstPage => {
  const explicit = String(query.chat_first_page || '')
  if (explicit === 'productDetail' || explicit === 'store' || explicit === 'messageCenter') {
    return explicit
  }
  if (query.goodsId) return 'productDetail'
  if (query.storeId) return 'store'
  return 'messageCenter'
}

/** 卖家发商品 → 聊天内点击商详 source（5 段，| 分隔） */
export const buildSellerSendProductLiveChatSource = (
  firstPage: LiveChatFirstPage
) => [
  'liveChat',
  'sellerSendProductLiveChat',
  '',
  firstPage,
  LIVE_CHAT_LATEST_PAGE
].join('|')

/** 买家发商品 → 聊天内点击商详 source（沿用原 source，latest_page 固定 liveChat） */
export const buildLiveChatBuyerProductClickSource = (productSource?: string): string => {
  const query = buildListingQueryFromTrackFields(
    productSource ? { source: productSource } : null,
    { latestTrafficSourcePage: LIVE_CHAT_LATEST_PAGE }
  )
  return query.source || ''
}

/** 分享页 share-link 接口埋点参数（店铺/新品/秒杀/商详） */
export const buildShareLinkTrafficSourceParams = (
  sourceType: string
): Pick<IShareLinkType, 'internalLatestTrafficSourceModule' | 'internalLatestTrafficSourceType' | 'internalLatestTrafficSourcePage'> => {
  switch (sourceType) {
    case 'Listing':
      return {
        internalLatestTrafficSourceModule: 'share',
        internalLatestTrafficSourceType: 'productShare',
        internalLatestTrafficSourcePage: 'productDetail'
      }
    case 'Store':
      return {
        internalLatestTrafficSourceModule: 'share',
        internalLatestTrafficSourceType: 'storeShare',
        internalLatestTrafficSourcePage: 'store'
      }
    case 'NewArrival':
      return {
        internalLatestTrafficSourceModule: 'share',
        internalLatestTrafficSourceType: 'newArrvalShare',
        internalLatestTrafficSourcePage: 'newArrval'
      }
    case 'Flashsale':
      return {
        internalLatestTrafficSourceModule: 'share',
        internalLatestTrafficSourceType: 'flashSaleShare',
        internalLatestTrafficSourcePage: 'flashSale'
      }
    default:
      return {}
  }
}

/** 分享链接落地页 URL 参数 → 商详 source（5 段）；缺少 internal 参数时返回 null */
export const buildShareEntryListingSourceFromQuery = (
  query: Record<string, unknown> = {}
): string | null => {
  const sourceModel = String(query.internalLatestTrafficSourceModule || '')
  const sourceType = String(query.internalLatestTrafficSourceType || '')
  const latestPage = String(query.internalLatestTrafficSourcePage || '')

  if (!sourceModel || !sourceType || !latestPage || sourceModel !== 'share') {
    return null
  }

  return [
    sourceModel,
    sourceType,
    String(query.ksa || ''),
    '',
    latestPage
  ].join('|')
}

export const WAP_PRODUCT_DETAIL_DEEPLINK_LATEST_PAGE = 'wapProductDetailDeepLink'
export const WAP_PRODUCT_DETAIL_DIRECT_LATEST_PAGE = 'wapProductDetails'

/** WAP 商详 Open App deeplink 5 段来源 query 参数 */
export const buildWapProductDetailDeepLinkSourceQuery = (
  source?: string,
  trafficSourceMeta?: ISourceMeta | null
): Record<string, string> => {
  let [
    sourceModule = '',
    sourceType = '',
    sourceValue = '',
    sourceFirstPage = '',
    sourceLatestPage = ''
  ] = getTrafficSourceParts(source, trafficSourceMeta)

  if (!sourceModule) {
    return {
      sourceModule: 'Direct',
      sourceType: 'organic',
      sourceValue: '',
      sourceFirstPage: '',
      sourceLatestPage: WAP_PRODUCT_DETAIL_DIRECT_LATEST_PAGE
    }
  }

  if (sourceModule === 'searchProduct') {
    sourceLatestPage = WAP_PRODUCT_DETAIL_DEEPLINK_LATEST_PAGE
  }

  return {
    sourceModule,
    sourceType,
    sourceValue,
    sourceFirstPage,
    sourceLatestPage
  }
}

/** 支付结果页推荐商品 source（5 段，| 分隔） */
export const buildPayResultRecommendSource = (modelName = '') => [
  'recommend',
  'payResultRecommend',
  modelName || '',
  'payResult',
  'payResult'
].join('|')

/** 商详页推荐商品 source（5 段，| 分隔） */
export const buildProductDetailRecommendSource = (modelName = '') => [
  'recommend',
  'productDetailRecommend',
  modelName || '',
  'productDetail',
  'productDetail'
].join('|')

/** 商详页 Store Selective 商品 source（5 段，| 分隔） */
export const buildProductDetailStoreSpecifiedSource = () => [
  'store',
  'productDetailStoreSpecified',
  '',
  'productDetail',
  'productDetail'
].join('|')

/** 进入店铺页时的 first_page 场景（与 BI pageViewList 对齐） */
export const STORE_HOME_FIRST_PAGE_CODES = [
  'productDetail',
  'storeFollowerList',
  'cart',
  'orderDetails',
  'orderList',
] as const

export type StoreHomeFirstPage = typeof STORE_HOME_FIRST_PAGE_CODES[number] | ''

/** 推断进入店铺页时的 first_page */
export const resolveStoreHomeFirstPage = (
  route?: Pick<RouteLocationNormalizedLoaded, 'query'>
): StoreHomeFirstPage => {
  const query = route?.query || useRoute().query
  const explicit = String(query.first_page || '')
  if ((STORE_HOME_FIRST_PAGE_CODES as readonly string[]).includes(explicit)) {
    return explicit as StoreHomeFirstPage
  }

  const [module, type, , firstPage, latestPage] = parseSourceToParts(String(query.source || ''))

  for (const code of STORE_HOME_FIRST_PAGE_CODES) {
    if (type === code || firstPage === code || latestPage === code) {
      return code
    }
  }

  if (module === 'productDetails') {
    return 'productDetail'
  }

  return ''
}

/** 跳转店铺页 query（带 first_page） */
export const buildStoreEntryQuery = (firstPage: Exclude<StoreHomeFirstPage, ''>) => ({
  first_page: firstPage
})

/** 店铺 Home CMS 组件 source_value 取值规则 */
export type StoreHomeCmsComponent =
  | 'advList'
  | 'goods'
  | 'title'
  | 'cutImage'
  | 'recommend'

export const resolveStoreHomeSourceValue = (
  component: StoreHomeCmsComponent,
  data?: {
    checked_name?: string
    title?: string
    modelName?: string
    algorithmModelName?: string
  }
): string => {
  if (!data) return ''

  switch (component) {
    case 'advList':
    case 'goods':
    case 'cutImage':
      return data.checked_name || ''
    case 'title':
      return data.title || ''
    case 'recommend':
      return data.modelName || data.algorithmModelName || 'recommend'
    default:
      return ''
  }
}

/** 店铺首页 Home 商品进商详 source（5 段，| 分隔）；URL 已带 source 时以 URL 为准 */
export const buildStoreHomeListingSource = (
  sourceValue: string,
  firstPage?: StoreHomeFirstPage,
  route?: Pick<RouteLocationNormalizedLoaded, 'query'>
) => {
  const query = route?.query || useRoute().query
  const shareSource = buildShareEntryListingSourceFromQuery(query as Record<string, unknown>)
  if (shareSource) return shareSource

  const urlSource = String(query.source || '')
  if (urlSource) return urlSource

  return [
    'store',
    'storeHome',
    sourceValue || '',
    firstPage || resolveStoreHomeFirstPage(route),
    'store'
  ].join('|')
}

/** 店铺 Products Tab 商品进商详 source（5 段，| 分隔）；URL 已带 source 时以 URL 为准 */
export const buildStoreAllProductListingSource = (
  sourceValue: string,
  firstPage?: StoreHomeFirstPage,
  route?: Pick<RouteLocationNormalizedLoaded, 'query'>
) => {
  const query = route?.query || useRoute().query
  const shareSource = buildShareEntryListingSourceFromQuery(query as Record<string, unknown>)
  if (shareSource) return shareSource

  const urlSource = String(query.source || '')
  if (urlSource) return urlSource

  return [
    'store',
    'storeAllProduct',
    sourceValue || '',
    firstPage || resolveStoreHomeFirstPage(route),
    'store'
  ].join('|')
}

/** 店铺内搜索结果页 → 商详 source（5 段，| 分隔）；搜索输入页跳转时也可复用（不传 options） */
export const buildStoreSearchListingSource = (
  keyword: string,
  route?: Pick<RouteLocationNormalizedLoaded, 'query' | 'params'>,
  options?: {
    suggestionUsed?: boolean
    keywordsSuggestions?: string[]
  }
) => {
  const currentRoute = route || useRoute()
  const query = currentRoute.query as Record<string, unknown>
  const shareSource = buildShareEntryListingSourceFromQuery(query)
  if (shareSource) return shareSource

  const firstPage = resolveStoreHomeFirstPage(currentRoute)
  const isStoreCategory = Boolean(query.storeId && query.storeCategoryIds)
  const sourceType = isStoreCategory ? 'storeCategory' : 'storeSearch'

  const resolveSourceValue = () => {
    if (!isStoreCategory) return keyword || ''

    const rawSource = typeof query.source === 'string' ? decodeURIComponent(query.source) : ''
    const [, , valueFromSource] = parseSourceToParts(rawSource)
    if (valueFromSource) return valueFromSource

    const categoryName = currentRoute.params?.categoryName
    if (categoryName) return String(categoryName)

    return keyword || ''
  }
  const sourceValue = resolveSourceValue()

  if (options?.suggestionUsed && options.keywordsSuggestions?.length) {
    return [
      'store',
      'correctionSearch',
      options.keywordsSuggestions[0] || sourceValue || '',
      firstPage || 'searchProductResults',
      'searchProductResults'
    ].join('|')
  }

  return [
    'store',
    sourceType,
    sourceValue,
    firstPage,
    'searchProductResults'
  ].join('|')
}

/** 店铺 Category Tab 跳转店铺内搜索结果 source（5 段，| 分隔） */
export const buildStoreCategoryNavigationSource = (
  categoryName: string,
  route?: Pick<RouteLocationNormalizedLoaded, 'query'>
) => [
  'store',
  'storeCategory',
  categoryName || '',
  resolveStoreHomeFirstPage(route),
  'searchProductResults'
].join('|')

/** @deprecated 使用 buildHomeBannerSource */
export const buildMainIconBannerSource = (iconTitle: string, cmsType: string) =>
  buildHomeBannerSource('mainIconBanner', iconTitle, cmsType)

export interface ICmsGoToOptions {
  /** 埋点场景：homeMainIconBanner 金刚区 / homeCenterBanner 首页轮播 */
  trafficSourceScene?: 'homeMainIconBanner' | 'homeCenterBanner'
  /** source 第 3 段 source_value（可为空） */
  sourceValue?: string
}

export type CategoryNavigationType = 'hotCategory' | 'allCategory'
export type CategorySearchResultPage = 'searchProductResults' | 'searchStoreResults'
export type CategoryTrafficLatestPage = CategorySearchResultPage | 'hotCategoryFragment'

/** 类目导航 source（5 段，| 分隔） */
export const buildCategoryNavigationSource = (
  type: CategoryNavigationType,
  categoryName: string,
  latestPage: CategoryTrafficLatestPage = 'searchProductResults',
  route?: Pick<RouteLocationNormalizedLoaded, 'query'>
) => {
  if (type === 'allCategory' && isMessageCenterCategorySearchEntry(route)) {
    return buildMessageCenterCategorySearchSource(categoryName)
  }

  const firstPage = type === 'hotCategory' ? 'hotCategoryFragment' : 'allCategoriesNavigation'
  return [
    'categoryNavigation',
    type,
    categoryName || '',
    firstPage,
    latestPage
  ].join('|')
}

/** 类目 bindUrl 跳转：专题页时带上 5 段 source（latestPage=marketingCms） */
export const jumpCategoryBindUrl = (
  bindUrl: string,
  type: CategoryNavigationType,
  categoryName: string,
  route?: Pick<RouteLocationNormalizedLoaded, 'query'>
) => {
  if (!bindUrl) return
  const source = buildCategoryNavigationSource(type, categoryName, 'searchProductResults', route)
  window.location.href = withMarketingCmsSource(bindUrl, source)
}

export const formatStringToURL = (str: string): string => {
  const sts = str.replace(/[^0-9a-zA-Z_\u4e00-\u9fa5 ]/g, '').split(' ')

  // return sts.map(c => c.toLowerCase()).filter(c => c !== '').join('-')
  return sts.filter(c => c !== '').join('-')
} 

/**
 * 8位随机id，碰撞概率
 * 假设每秒生成：
    1000个ID → 约8天后有1%碰撞概率
    10,000个ID → 约20小时有1%碰撞概率
 * @returns 
 */
export function better8DigitId(uid?: number) {
  const timePart = performance.now().toString(36).replace('.','').substr(-3);
  const randomPart = Math.random().toString(36).substr(2, 5);
  return (timePart + randomPart).substr(0, 8);
}

export const toListingDetail = (
  id: number,
  queryParams: IToListingQuery = {},
  hasHref?: boolean
) => {
  const { title, source, type, skuId, from, isAd, isNewTag, isChoiceTag, requestId, algorithmModelName, shortTitle } = queryParams
  const sourceMeta = source ? parseSourceToMeta(source) : undefined

  const idParams: string[] = [String(id)]

  if(shortTitle || title) {
    idParams.push(formatStringToURL((shortTitle || title) as string))
  }

  const _key = `${id}_${useCookie('kilimall_cache_key').value}`
  const path = `/listing/${idParams.join('-')}`
  const toData = {
    url: useRouter().resolve({ path: path }).href,
    key: _key,
    cookie: {
      id: id,
      query: {
        type,
        skuId: skuId ? Number(skuId) : '',
        from,
        ...(sourceMeta ? { sourceMeta } : source ? { source } : {}),
        isAd: isAd === 'ad' || isAd === true,
        isNewTag: !!isNewTag,
        isChoiceTag: !!isChoiceTag,
        requestId: requestId || '',
        algorithmModelName: algorithmModelName || ''
      }
    }
  }

  if(!hasHref) {
    setUrlCache(toData.cookie, toData.key).then(k => {
      useRouter().push({ path: path }) 
    })
  }

  return toData
}

export interface IGetListingDetailHrefOptions {
  /** 是否写入 url-cache（默认 true；列表页仅生成 href 时可传 false） */
  writeCache?: boolean
}

export const getListingDetailHref = (
  id: number,
  queryParams: IToListingQuery = {},
  options: IGetListingDetailHrefOptions = {}
) => {
  const toData = toListingDetail(id, queryParams, true)
  let cacheWrite: Promise<void> | undefined

  if (options.writeCache !== false && import.meta.client) {
    cacheWrite = setUrlCache(toData.cookie, toData.key)
      .then(() => {})
      .catch((e) => {
        console.error('setUrlCache failed:', e)
      })
  }

  return { ...toData, cacheWrite }
}

/**
  * 节流函数
  * @param {*} fn 回调
  * @param {*} delay 执行间隔时间 ms
  * @returns
*/
export const throttle = function (fn: Function, delay: number = 15) {
  let timer: NodeJS.Timeout | null = null

  return function (this: any, ...args: any) {
    if (!timer) {
      const context = this

      timer = setTimeout(() => {
        fn.apply(context, args)

        timer = null
      }, delay)
    }
  }
}

export const debounce = function (fn: Function, delay: number = 200) {
  let timer: NodeJS.Timeout | null = null

  return function (this: any, ...args: any) {
    if (timer) {
      clearTimeout(timer)
    }

    const context = this
    timer = setTimeout(() => {
      fn.apply(context, args)
    }, delay)
  }
}

/**
 * 解析路径查询参数
 * @param e path https://www.xxx.com/?aaa=111
 * @returns { aaa: '111' }
 */
export function getQuerys(e: string): IAnyObject {
  if (!e) return {};
  var t: IAnyObject = {},
    r = [],
    n = "",
    a = "";
  try {
    var i: any[] = [];
    if (e.indexOf("?") >= 0 && (i = e.substring(e.indexOf("?") + 1, e.length).split("&")), i.length > 0) for (var o in i) n = (r = i[o].split("="))[0],
      a = r[1],
      t[n] = a
  } catch (s) {
    t = {}
  }
  return t
}

/**
 * 毫秒 转 时 分 秒(补0)
 * @param t number 毫秒
 * @returns {
    hour: string,
    minute: string,
    second: string
  }
*/
interface ITimeToHMS { hour: string, minute: string, second: string }
const timeToHMS = (t: number): ITimeToHMS => {
  const time = t / 1000;
  let hour = Math.floor(time / 60 / 60).toString();
  hour = hour.toString().padStart(2, "0");
  let minute = (Math.floor(time / 60) % 60).toString();
  minute = minute.toString().padStart(2, "0");
  let second = (Math.floor(time) % 60).toString();
  second = second.toString().padStart(2, "0");

  const obj = { hour, minute, second }

  return obj
}

// https://image.kilitest.com//comment/kili_d0763005-20a4-4552-b7ac-e010525712171710231611362_360.jpg.webp#?x-image-process=image/format,webp
export const getImageSourceUrl = (url: string): string => {
  let sourceSrc = url.split('?')[0]
  const dotWebpIdx = sourceSrc.indexOf('.webp')

  if (dotWebpIdx >= 0) {
    sourceSrc = sourceSrc.substr(0, dotWebpIdx)
  }

  return sourceSrc
}
/**
 * 图片转webp
*/
const configuredImageHost = new URL(domain.S3_IMAGE_URL).hostname
export const cvWebpFn = (url: string, config?: IImageTransformConfig) =>
  transformImageUrl(url, config, !!IsWebp().value, configuredImageHost)

export const currencySymbol = (): string => {
  const regionId = useCurrentCountry().value
  const regions = useCountrys().value
  const curReg = regions.find((c: region.IRegionItem) => c.id === Number(regionId))

  return curReg?.currencySymbol || ''
}

/** 货币转换千分位带，号 */
export const toThousandsFn = (price: string | number): string | number => {
  if (!price) return 0
  let res_price = price.toString().replace('.00', '')
  return res_price.replace(/(\d{1,3})(?=(\d{3})+(?:$|\.))/g, "$1,")
}

export const filterEmptyParams = (params: any, keepZero: boolean = false) => {
  const newParams: any = {}

  Object.keys(params).forEach((k: any) => {
    const v = params[k]
    if (v || (keepZero && v === 0) || typeof v === 'boolean') {
      newParams[k] = v
    }
  })

  return newParams
}

/**
 * 自定义格式化日期
 * @param {Date String} date[必须] Date() 对象 或 日期字符串
 * @param {String} format[可选] （默认值：dd/MM/yyyy hh:mm:ss）支持 yyyy-MM-dd hh:mm:ss等常用日期格式
 * @param {String} clearTimeZone[可选] 去掉时区字符串 '2022-09-09T12:00:00.00000Z' -> 2022-09-09T12:00:00
 * @returns
 */
export function formatDate(date: string | Date, fmt = 'yyyy-MM-dd hh:mm:ss') { // author: meizz
  if (!date) {
    return ''
  }
  if (getTypeof(date) === 'String') {
    date = new Date(date)
  }

  const dt = date as Date
  const m = dt.getMonth()
  const o: any = {
    'M+': m + 1, // 月份
    'd+': dt.getDate(), // 日
    'h+': dt.getHours(), // 小时
    'm+': dt.getMinutes(), // 分
    's+': dt.getSeconds(), // 秒
    'q+': Math.floor((dt.getMonth() + 3) / 3), // 季度
    S: dt.getMilliseconds(), // 毫秒
    E: monthEN[m]
  }
  if (/(y+)/.test(fmt)) {
    fmt = fmt
      .replace(
        RegExp.$1,
        (`${dt.getFullYear()}`).substr(4 - RegExp.$1.length)
      )
  }

  Object.keys(o).forEach((k: any) => {
    if (new RegExp(`(${k})`).test(fmt)) {
      fmt = fmt
        .replace(
          RegExp.$1,
          (RegExp.$1.length === 1)
            ? (o[k])
            : ((`00${o[k]}`).substr((`${o[k]}`).length))
        )
    }
  })
  return fmt
}

/**
 * 根据秒计算时间
 * @param time 秒
 * @returns 
 */
export function formatCountdownByTime(time?: number): string {
  if (!time || time < 0) {
    return '0.'
  }

  const d = Math.floor(time / 60 / 60 / 24)
  const dd = d < 10 ? '0' + d : d

  const h = Math.floor(time / 60 / 60 % 24)
  const hh = h < 10 ? '0' + h : h

  const m = Math.floor(time / 60 % 60)
  const mm = m < 10 ? '0' + m : m
  const s = Math.floor(time % 60)
  const ss = s < 10 ? '0' + s : s
  if (d > 0) {
    // return `${dd} day ${hh} hours ${mm} mins ${ss} seconds`
    return `${d} d ${h} h ${m} min ${s} s.`
  } else {
    return `${h} h ${m} min ${s} s.`
  }
}

/**
 * 计算折扣
 * @param salePrice 售价
 * @param delPrice 原价
 * @returns 
 */
export const getDiscount = (salePrice: number, delPrice: number): string => {
  let discount = '';
  (delPrice - salePrice)
    / delPrice
    >= 0.2
    ? discount = `${((delPrice - salePrice) / delPrice * 100).toFixed(0)}%`
    : discount = `${currencySymbol()} ${(delPrice - salePrice).toString().replace(/\d{1,3}(?=(\d{3})+$)/g, '$&,')}`

  return discount
}

export const getCountry = (): region.IRegionItem => {
  const regionId = useCurrentCountry().value

  return useCountrys().value.find((c: region.IRegionItem) => c.id === regionId)!
}

/**
 * Blob 类型转 JSON
 * @param data Blob
 * @returns  Promise<IAnyObject>
 */
export const blobToJson = (data: Blob): Promise<IAnyObject> => {
  if (getTypeof(data) !== 'Blob') {
    return Promise.resolve(data)
  }
  const reader = new FileReader()
  reader.readAsText(data, 'utf-8')

  return new Promise((resolve: (res: any) => void) => {
    reader.onload = function () {
      const res = JSON.parse(reader.result as string)

      return resolve(res)
    }
  })
}

/**
* 金额格式保留小数位
* @param num 数值
* @param decimal 设置保留的小数位数
* @param str 金额面值
*/
export const parsePrice = (num: any, decimal: number = 2) => {
  num = parseFloat((num + "").replace(/[^\d\.-]/g, "")).toFixed(decimal) + "";
  let l = num.split(".")[0].split("").reverse();
  let r = num.split(".")[1];
  let t = "";
  for (let i = 0; i < l.length; i++) {
    t += l[i];
  }
  return `${currencySymbol()}  ${t.split("").reverse().join("")}.${r} `;
}

/**
 * 写入购物车快捷列表
 * @param listing 搜索栏购物车快捷列表
 */
const setShoppingCartLaconicList = (
  listing: IShoppingCartLaconic,
  type: 'del' | 'add' = 'add'
) => {
  const curIdx = useShoppingCartLaconicList()
    .value
    .findIndex((c: IShoppingCartLaconic) =>
      `${c.listingId}_${c.skuId}` === `${listing.listingId}_${listing.skuId}`)


  if (curIdx < 0 && type === 'add') {
    useShoppingCartLaconicList().value.push(listing)
  }

  if (curIdx >= 0 && type === 'del') {
    useShoppingCartLaconicList().value.splice(curIdx, 1)
  }
}

/**
 * 格式化购物车数据
 * @param storeList 购物车店铺列表
 * @returns 
 */
export const formatShoppingCartList = (storeList: any[]): {
  storeProducts: any[]
  invalidStoresProducts: any[]
  invalidStores: IAnyObject
  stores: IAnyObject
} => {
  const isActivityExpired = (item: any): boolean => {
    if (!item.activity.activityId) {
      return false
    }
    if (item.activity.status !== 1) {
      return true
    }

    if (item.activity.endTime) {
      const endTime = new Date(item.activity.endTime).getTime()
      const nowTime = new Date().getTime()

      return nowTime > endTime || item.activity.status === 2
    }

    return false
  }

  const invalidStores: IAnyObject = {};
  const stores: IAnyObject = {};
  (storeList || []).forEach((c: any) => {
    // stores.status：店铺状态--1.开业；2.歇业；3.关店。该值仅为3时其店铺下所有商品为已关店失效商品；
    if (c.status === 3) {
      invalidStores[c.storeId] = c
    } else {
      c.items.forEach((sku: any) => {
        // skuStatus: 商品状态--1.可用；0.不可用；-1.手动下架；-99.已删除。该值不为1时均为已下架失效商品；
        // stock: 商品库存--正整数表示商品库存；-1.无对应仓库；0.有仓库但是库存为0。该值不为正整数时为无库存失效商品。
        if (
          sku.skuStatus !== 1 ||
          sku.stock <= 0 ||
          isActivityExpired(sku)
        ) {
          sku.checked = false
          if (!invalidStores[c.storeId]) {
            invalidStores[c.storeId] = { ...c, items: [] }
          }

          invalidStores[c.storeId].items.push(sku)
        } else {
          if (!stores[c.storeId]) {
            stores[c.storeId] = { ...c, items: [] }
          }

          stores[c.storeId].items.push(sku)
        }
      })
    }
  })

  const storeProducts = Object.keys(stores).map((k) => stores[k])
  const invalidStoresProducts = Object.keys(invalidStores).map((k) => invalidStores[k])

  if (process.client) {
    storeProducts.forEach((item: any) => {
      item.items.forEach((sku: any) => {
        setShoppingCartLaconicList({
          skuId: sku.skuId,
          listingId: sku.listingId,
          productImage: sku.image,
          productTitle: sku.title,
          spec: sku.spec || '',
          salePrice: sku.salePrice
        })
      })
    })
  }

  return {
    storeProducts,
    invalidStoresProducts,
    invalidStores,
    stores
  }
}

export const getHotKeysJumpUrl = (
  item: { linkType: any, linkValue: any, keyword?: string },
  isTrack: boolean = true, // 是否埋点
  trackSource?: string,
  /** listing/sku 是否写入 url-cache；仅真正跳转时传 true。模板里绑 :href 时必须 false，否则每次重渲染都会打 set-params */
  options: { writeCache?: boolean } = {}
): {
  href: string,
  raw?: RouteLocationRaw | null,
  cacheWrite?: Promise<void>
} => {
  const router = useRouter()
  let source = isTrack ? trackSource || `searchProduct|hotSearch|${item.keyword || item.linkValue || ''}` : ''
  if (source) {
    source = applyHotKeysSourceLatestPage(source, item.linkType)
  }
  let _fullPath: string = ''
  let raw: RouteLocationRaw | null = null
  let cacheWrite: Promise<void> | undefined
  const writeCache = options.writeCache === true
  switch (item.linkType) {
    case 1:
      raw = { path: _fullPath }

      if (/^http(s)?:\/\//.test(item.linkValue)) {
        _fullPath = item.linkValue
      } else {
        _fullPath = router.resolve({ path: item.linkValue }).href
      }

      // 专题页 /special、/c：带上 source，latestPage 固定 marketingCms
      if (source && isMarketingCmsUrl(_fullPath || item.linkValue)) {
        _fullPath = withMarketingCmsSource(_fullPath || item.linkValue, source)
        raw = null
      }

      break
    case 2:
      raw = { path: '/search', query: { q: item.linkValue, page: 1, source } }

      _fullPath = router.resolve(raw).href
      break
    case 3:
      // 类目跳转
      raw = { path: '/search', query: { id: item.linkValue, source } }

      if (item.keyword) {
        raw = { path: `/category/${formatStringToURL(item.keyword)}`, query: { id: item.linkValue, source } }
      }
      _fullPath = router.resolve(raw).href
      break
    case 5: {
      // Listing 跳转
      const detail = getListingDetailHref(item.linkValue, { source }, { writeCache })
      _fullPath = detail.url
      cacheWrite = detail.cacheWrite
      break
    }
    case 6:
      raw = { path: `/store/${item.linkValue}`, query: { source } }
      _fullPath = router.resolve(raw).href
      break
    // case 7:
    //   // 品牌页，暂时没有
    //   break
    // case 8:
    //   break
    // case 9:
    //   break
    // Listing Detail By SKU
    case 10: {
      // SKU ID 跳转
      const detail = getListingDetailHref(item.linkValue, { type: 'sku', source }, { writeCache })
      _fullPath = detail.url
      cacheWrite = detail.cacheWrite
      break
    }
  }

  return { raw, href: _fullPath, cacheWrite }
}

/**
 * facebook分享
 * @param mates declare IFacebookShareMates { url: string, title: string, image: string, description?: string, type: string }
 */
export const setFacebookShareMates = (mates: IFacebookShareMates) => {
  const _mates: any[] = [{ property: 'fb:app_id', content: '951665321609204' }]

  Object.keys(mates).forEach((k) => {
    const _val = mates[k as keyof IFacebookShareMates]
    _mates.push({ property: `og:${k}`, content: _val })
  })

  useHead({ meta: _mates })
}

export const resetRouterBack = () => {
  const router = useRouter()
  const sourceBack = router.back

  router.back = () => {
    const back = router.options.history.state.back

    !back ? router.replace('/') : sourceBack()
  }
}

export const biGroupBanners = (avaBanners: IBanner[]): IBanner[] => {
  if(useUserInfo().value) {
    const { profile } = useUserInfo().value
    const groupId = profile?.lastUserGroupId
    
    if(groupId >= 0) {
      // 用户分群
      return avaBanners.filter(c => c.userGroupType === 0 || c.userGroupType === 2 || c.userGroupIds?.includes(groupId))
    } else {
      // 已登陆用户
      return avaBanners.filter(c => c.userGroupType === 0 || c.userGroupType === 2)
    }
  } else {
    // 未登录用户
    return avaBanners.filter(c => c.userGroupType === 0 || c.userGroupType === 1)
  }
}

export const filterBanners = (banners: IBanner[]): IBanner[] => {
  const avaBanners = banners.filter((item: IBanner) => {
    const { startTime, endTime, enabled } = item
    const curDt = new Date().getTime()

    if (startTime && endTime) {
      const endT = new Date(zeroToLocalZonetime(endTime)).getTime()
      const startT = new Date(zeroToLocalZonetime(startTime)).getTime()
      if (curDt < startT || curDt > endT) {
        return false
      }
    }

    return enabled
  })

  return biGroupBanners(avaBanners)
}

/**
 * 对象传入直接过滤掉null、''、undefined后返回
 * @param {对象} obj
 */
export const removeEmpty = (obj: any) => {
  Object.keys(obj).forEach((k) => (obj[k] === null || obj[k] === undefined || obj[k] === '') && delete obj[k])
}

export default defineNuxtPlugin(async (NuxtApp) => {
  resetRouterBack()

  const provide = {
    filterBanners,
    getTypeof,
    throttle,
    cvWebpFn,
    getImageSourceUrl,
    toThousandsFn,
    currencySymbol,
    debounce,
    filterEmptyParams,
    formatDate,
    getDiscount,
    getCountry,
    blobToJson,
    timeToHMS,
    getQuerys,
    formatCountdownByTime,
    parsePrice,
    setShoppingCartLaconicList,
    formatStringToURL,
    formatShoppingCartList,
    getHotKeysJumpUrl,
    setFacebookShareMates,
    toListingDetail,
    getListingDetailHref,
    removeEmpty,
    better8DigitId
  }
  return { provide }
})
